import { useState, useRef, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Eye, Download, Share2, AlertTriangle } from "lucide-react";
import { getCurrencySymbol } from "@/lib/currencyUtils";
import { PRODUCTION_DOMAIN } from "@/lib/constants";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { format } from "date-fns";
import { DatePickerField } from "./DatePickerField";
import { QuoteDocument } from "./QuoteDocument";
import { useReactToPrint } from "react-to-print";
import { Switch } from "@/components/ui/switch";
import { findBestProductMatch, ProductForMatching } from "@/lib/productMatchingUtils";
import { fetchRoomRatesForItems } from "@/lib/roomRateEnrichment";
import { TermsAndConditionsSelector } from "./TermsAndConditionsSelector";
import { InclusionsExclusionsSelector } from "./InclusionsExclusionsSelector";
import type { ItineraryItemForExtraction } from "@/lib/inclusionsExclusionsData";
import { HotelOptionMarginsCard } from "./quote/HotelOptionMarginsCard";
import { ParticipantOptionSelector } from "./ParticipantOptionSelector";
import { AdditionalFeesSection, AdditionalFee, calculateAdditionalFeesTotal, serializeAdditionalFees } from "./AdditionalFeesSection";
import { OptionalExtrasSection, OptionalExtra, serializeOptionalExtras, persistNewLibraryExtras } from "./OptionalExtrasSection";
import { applyQuoteDiscounts, baseItemCost, withClientPrices, blendedMarkupPercent, hasMarkupOverrides, tradeDiscountExcludedCost } from "@/lib/quoteChoices";
import { scaleItemsForPax, calculateItemTotalCost, hasAccommodationItems } from "@/lib/itineraryCostUtils";
import { computeGroupLeaderImpact, parseLeaderRoomOverrides } from "@/lib/groupLeaderUtils";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { AccommodationRoomOverridePanel } from "./itinerary/AccommodationRoomOverridePanel";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const quoteSchema = z.object({
  transport_discount_amount: z.string().optional(),
  discount_percentage: z.string()
    .refine((val) => val === "" || (!isNaN(parseFloat(val)) && parseFloat(val) >= 0), {
      message: "Discount must be a positive number"
    })
    .refine((val) => val === "" || parseFloat(val) <= 100, {
      message: "Discount cannot exceed 100%"
    })
    .optional(),
  discount_fixed_amount: z.string().optional(),
  markup_percentage: z.string()
    .refine((val) => !isNaN(parseFloat(val)) && parseFloat(val) >= 0, {
      message: "Markup must be a positive number"
    })
    .refine((val) => parseFloat(val) <= 100, {
      message: "Markup cannot exceed 100%"
    }),
  valid_until: z.string().optional(),
  prepared_for: z.string().optional(),
  inclusions: z.string().optional(),
  exclusions: z.string().optional(),
  terms_and_conditions: z.string().max(5000, "Terms must be less than 5000 characters").optional(),
  notes: z.string().max(2000, "Notes must be less than 2000 characters").optional(),
  show_line_items: z.boolean().default(true),
  hide_markup: z.boolean().default(false),
  allow_pax_scaling: z.boolean().default(false),
  is_white_label: z.boolean().default(false),
  hide_programme_inclusions_exclusions: z.boolean().default(false),
});

interface QuotePreviewDialogProps {
  leadId: string;
  totalCost: number;
  currency: string;
  paymentTerms?: string | null;
  leadStatus?: string;
  lead?: any;
}

// Statuses where quote generation should be blocked
const BLOCKED_FOR_QUOTE = ['completed', 'reporting', 'cancelled', 'aborted'];

export const QuotePreviewDialog = ({ leadId, totalCost, currency, paymentTerms, leadStatus, lead }: QuotePreviewDialogProps) => {
  // Check if quote generation is blocked - must be before hooks for early return
  const isBlocked = leadStatus && BLOCKED_FOR_QUOTE.includes(leadStatus);
  
  const [open, setOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [generatedQuoteId, setGeneratedQuoteId] = useState<string | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [selectedOptionQuantity, setSelectedOptionQuantity] = useState<number | null>(null);
  const [selectedOptionLabel, setSelectedOptionLabel] = useState<string | null>(null);
  const [additionalFees, setAdditionalFees] = useState<AdditionalFee[]>([]);
  const [optionalExtras, setOptionalExtras] = useState<OptionalExtra[]>([]);
  const [roomOverrides, setRoomOverrides] = useState<Record<string, number>>({});
  const [accomPanelOpen, setAccomPanelOpen] = useState(false);
  const queryClient = useQueryClient();
  const printRef = useRef<HTMLDivElement>(null);
  
  const form = useForm<z.infer<typeof quoteSchema>>({
    resolver: zodResolver(quoteSchema),
    defaultValues: {
      transport_discount_amount: "0",
      discount_percentage: "0",
      discount_fixed_amount: "0",
      markup_percentage: "0",
      valid_until: "",
      prepared_for: "",
      inclusions: "",
      exclusions: "",
      terms_and_conditions: paymentTerms || "",
      notes: "",
      show_line_items: true,
      hide_markup: false,
      allow_pax_scaling: false,
      is_white_label: false,
      hide_programme_inclusions_exclusions: false,
    },
  });

  // Prefill inclusions/exclusions/T&Cs from the most recent quote for this lead
  const { data: lastQuote, isFetched: lastQuoteFetched } = useQuery({
    queryKey: ["last-quote-text", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quotations")
        .select("inclusions, exclusions, terms_and_conditions")
        .eq("lead_id", leadId)
        .is("archived_at", null)
        .eq("document_type", "quote")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  const prefilledRef = useRef(false);
  const [prefilledFromPrevious, setPrefilledFromPrevious] = useState(false);
  const [prefillSettled, setPrefillSettled] = useState(false);
  const hideToggledRef = useRef(false);
  useEffect(() => {
    if (!open) {
      prefilledRef.current = false;
      setPrefilledFromPrevious(false);
      setPrefillSettled(false);
      return;
    }
    if (prefilledRef.current) return;
    if (!lastQuoteFetched) return;
    const hasAny = !!(lastQuote && (lastQuote.inclusions || lastQuote.exclusions || lastQuote.terms_and_conditions));
    if (hasAny) {
      form.setValue("inclusions", lastQuote!.inclusions || "", { shouldDirty: false });
      form.setValue("exclusions", lastQuote!.exclusions || "", { shouldDirty: false });
      if (lastQuote!.terms_and_conditions) {
        form.setValue("terms_and_conditions", lastQuote!.terms_and_conditions, { shouldDirty: false });
      }
      setPrefilledFromPrevious(true);
    }
    prefilledRef.current = true;
    setPrefillSettled(true);
  }, [open, lastQuote, lastQuoteFetched, form]);

  // Fetch itinerary items with supplier and product images - only items included in quote
  const { data: itineraryItems = [], isLoading: isLoadingItineraryItems } = useQuery({
    queryKey: ["itinerary-items-with-images", leadId],
    queryFn: async () => {
      const { data: items, error } = await supabase
        .from("itinerary_items")
        .select("*")
        .eq("lead_id", leadId)
        .neq("include_in_quote", false)
        .order("date", { ascending: true, nullsFirst: false })
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });
      
      if (error) throw error;
      
      // Fetch all products with images (include id for direct linking)
      const { data: products } = await supabase
        .from("products")
        .select("id, product_name, hero_image_url, secondary_image_url, image_url_3, image_url_4");
      
      // Fetch all suppliers with images
      const { data: suppliers } = await supabase
        .from("suppliers")
        .select("name, hero_image_url, website, secondary_image_url, image_url_3, image_url_4");

      // Fetch any room rates referenced by accommodation items
      const roomRatesMap = await fetchRoomRatesForItems(items as any);

      // Enrich items with matched product and supplier data using score-based matching
      const itemsWithImages = items.map(item => {
        // Priority 1: Direct product link via product_id
        const directlyLinkedProduct = item.product_id
          ? products?.find(p => p.id === item.product_id)
          : null;
        
        if (directlyLinkedProduct) {
          // Trim supplier name for matching (still need for fallback)
          const normalizedSupplierName = (item.supplier || '').trim().toLowerCase();
          const matchedSupplier = suppliers?.find((s) => 
            (s.name || '').trim().toLowerCase() === normalizedSupplierName
          );
          
          return {
            ...item,
            products: directlyLinkedProduct,
            suppliers: matchedSupplier || null,
            display_title: directlyLinkedProduct.product_name || item.title,
            display_description: item.description,
            is_custom_item: false,
            selected_time_slot: item.selected_time_slot as { label: string; start: string; end: string } | null,
            room_rate: (item as any).room_rate_id ? roomRatesMap.get((item as any).room_rate_id) || null : null,
          };
        }

        // Priority 2: Fall back to score-based fuzzy matching (finds best match, not first match)
        const normalizedItemTitle = (item.title || '').trim().toLowerCase();
        const normalizedItemDesc = (item.description || '').trim().toLowerCase();

        const matchedProduct = item.item_type === 'accommodation'
          ? null
          : findBestProductMatch(
              normalizedItemTitle,
              normalizedItemDesc,
              products as ProductForMatching[] | null
            );

        // Trim supplier name for matching
        const normalizedSupplierName = (item.supplier || '').trim().toLowerCase();
        let matchedSupplier = suppliers?.find((s) => 
          (s.name || '').trim().toLowerCase() === normalizedSupplierName
        ) || null;
        
        // Fallback: match supplier by item title (accommodation items ARE suppliers)
        if (!matchedSupplier) {
          matchedSupplier = suppliers?.find((s) =>
            (s.name || '').trim().toLowerCase() === normalizedItemTitle
          ) || null;
        }
        
        // Determine display title and description
        // For custom items, use custom fields; for database items, use product data
        const isCustomItem = !matchedProduct && (item as any).custom_title;
        const displayTitle = isCustomItem 
          ? (item as any).custom_title 
          : (matchedProduct?.product_name || item.title);
        const displayDescription = isCustomItem
          ? (item as any).custom_description
          : item.description;
        
        console.log('Quote Item Data:', {
          title: item.title,
          description: displayDescription,
          supplier: item.supplier,
          matchedProduct,
          matchedSupplier,
          matchedVia: matchedProduct 
            ? (matchedProduct.product_name === item.title ? 'title' : 'description')
            : 'no match',
          isCustomItem,
          displayTitle,
        });
        
        return {
          ...item,
          products: matchedProduct || null,
          suppliers: matchedSupplier || null,
          display_title: displayTitle,
          display_description: displayDescription,
          is_custom_item: isCustomItem,
          // Cast selected_time_slot from Json to the expected type
          selected_time_slot: item.selected_time_slot as { label: string; start: string; end: string } | null,
          room_rate: (item as any).room_rate_id ? roomRatesMap.get((item as any).room_rate_id) || null : null,
        };
      });
      
      return itemsWithImages;
    },
    enabled: previewMode,
  });

  // Fetch the generated quote
  const { data: generatedQuote } = useQuery({
    queryKey: ["quote", generatedQuoteId],
    queryFn: async () => {
      if (!generatedQuoteId) return null;
      const { data, error } = await supabase
        .from("quotations")
        .select("*, leads!quotations_lead_id_fkey(*, business_brands:brand_id(*))")
        .eq("id", generatedQuoteId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!generatedQuoteId && previewMode,
  });

  // Lightweight always-on query for live pax scaling (independent of previewMode).
  // Also used by the inclusions selector so it can populate immediately when the
  // dialog is opened (before the heavier preview-mode query runs).
  const { data: scalingItems = [], isLoading: isLoadingScalingItems, refetch: refetchScalingItems } = useQuery({
    queryKey: ["itinerary-items-scaling", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("itinerary_items")
        .select("id, title, supplier, occupancy_type, accommodation_type, room_guest_type, cost, manual_price, quantity, item_type, check_in, check_out, service_start, service_end, universal_fee_kind, staff_categories, include_in_quote, leader_excluded, inclusions, exclusions, pricing_treatment, alternative_to_item_id, markup_percentage, exclude_from_trade_discount")
        .eq("lead_id", leadId)
        .neq("include_in_quote", false);
      if (error) throw error;
      return data || [];
    },
    // Always reload on open so the previewed total reflects the latest item edits.
    enabled: open,
    staleTime: 0,
    refetchOnMount: "always",
  });

  // Single-activity quotes default to hiding the programme-level lists — the
  // item's own inclusions/exclusions already render beneath it on the quote.
  useEffect(() => {
    if (!open) {
      hideToggledRef.current = false;
      return;
    }
    if (hideToggledRef.current) return;
    const included = (scalingItems as any[]).filter((i) => i.include_in_quote !== false);
    if (included.length === 1 && !form.getValues("hide_programme_inclusions_exclusions")) {
      form.setValue("hide_programme_inclusions_exclusions", true, { shouldDirty: false });
    }
  }, [open, scalingItems, form]);

  // Group leader context (folded into the paying travellers' per person price)
  const { data: leaderContext } = useQuery({
    queryKey: ["lead-group-leader", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("group_leader_count, group_leader_room_overrides")
        .eq("id", leadId)
        .maybeSingle();
      if (error) throw error;
      return data as { group_leader_count: number | null; group_leader_room_overrides: unknown } | null;
    },
  });

  const basePax = lead?.pax_count;
  const effectivePax = selectedOptionQuantity || basePax;
  const isScaling = !!(basePax && effectivePax && basePax !== effectivePax);

  const leaderCount = Number(leaderContext?.group_leader_count || 0);
  const leaderRoomOverrides = useMemo(
    () => parseLeaderRoomOverrides(leaderContext?.group_leader_room_overrides),
    [leaderContext]
  );

  const accommodationItems = useMemo(
    () => (scalingItems as any[]).filter((i) => i.item_type === 'accommodation'),
    [scalingItems]
  );

  const applyOverrides = <T extends { id?: string; item_type?: string; quantity?: number | null }>(items: T[]): T[] =>
    items.map((it) => {
      if (it.item_type === 'accommodation' && it.id && roomOverrides[it.id] !== undefined) {
        return { ...it, quantity: roomOverrides[it.id] };
      }
      return it;
    });

  const leaderImpact = useMemo(
    () =>
      computeGroupLeaderImpact(applyOverrides(scalingItems as any) as any, {
        payingPax: effectivePax,
        leaderCount,
        roomOverrides: leaderRoomOverrides,
      }),
    [scalingItems, effectivePax, leaderCount, leaderRoomOverrides, roomOverrides]
  );

  const scaledTotal = useMemo(() => {
    const hasOverrides = Object.keys(roomOverrides).length > 0;
    const base = (() => {
      if (!isScaling && !hasOverrides) return totalCost;
      if (!scalingItems.length) return totalCost;
      const scaled = isScaling
        ? scaleItemsForPax(scalingItems as any, basePax, effectivePax)
        : (scalingItems as any[]);
      const merged = applyOverrides(scaled);
      return merged.reduce((sum: number, it: any) => sum + baseItemCost(it), 0);
    })();
    return base + leaderImpact.netTotal;
  }, [isScaling, scalingItems, basePax, effectivePax, totalCost, roomOverrides, leaderImpact]);

  const showAccomWarning = isScaling && hasAccommodationItems(scalingItems as any);

  const discountExcludedCost = useMemo(() => {
    const scaled = isScaling
      ? scaleItemsForPax(scalingItems as any, basePax, effectivePax)
      : (scalingItems as any[]);
    return tradeDiscountExcludedCost(applyOverrides(scaled));
  }, [isScaling, scalingItems, basePax, effectivePax, roomOverrides]);


  const usesItemMarkups = useMemo(() => hasMarkupOverrides(scalingItems as any[]), [scalingItems]);

  /**
   * Pricing for the quote. When `freshItems` (the exact items being snapshotted,
   * already pax-scaled) are given, price from them so the saved total always
   * matches the snapshot — the cached preview items can lag behind recent edits.
   */
  const calculatePricing = (freshItems?: any[]) => {
    const transportDiscount = parseFloat(form.watch("transport_discount_amount") || "0");
    const discountPct = parseFloat(form.watch("discount_percentage") || "0");
    const discountFixed = parseFloat(form.watch("discount_fixed_amount") || "0");
    const formMarkupPct = parseFloat(form.watch("markup_percentage") || "0");
    const markupItems = freshItems ?? (scalingItems as any[]);
    const markupPct = hasMarkupOverrides(markupItems)
      ? blendedMarkupPercent(markupItems, formMarkupPct)
      : formMarkupPct;
    const subtotal = freshItems
      ? freshItems.reduce((sum: number, it: any) => sum + baseItemCost(it), 0) + leaderImpact.netTotal
      : scaledTotal;

    const discounts = applyQuoteDiscounts({
      subtotal,
      excludedCost: freshItems ? tradeDiscountExcludedCost(freshItems) : discountExcludedCost,
      transportDiscount,
      tradeDiscountPercentage: discountPct,
      tradeDiscountFixed: discountFixed,
    });
    const pctDiscountAmount = discounts.percentageAmount;
    const appliedDiscountFixed = discounts.fixedAmount;
    const costBeforeMarkup = discounts.costAfterDiscounts;
    const additionalFeesTotal = calculateAdditionalFeesTotal(additionalFees, costBeforeMarkup);
    const markupBase = costBeforeMarkup + additionalFeesTotal;
    const markupAmount = markupBase * (markupPct / 100);
    const total = markupBase + markupAmount;

    return { subtotal, transportDiscount, pctDiscountAmount, discountFixed: appliedDiscountFixed, costBeforeMarkup, markupAmount, additionalFeesTotal, total, markupPct };
  };


  const generateQuoteMutation = useMutation({
    mutationFn: async (values: z.infer<typeof quoteSchema>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Fetch itinerary items to snapshot (only those included in quote)
      const { data: itemsToSnapshot, error: itemsError } = await supabase
        .from("itinerary_items")
        .select("*")
        .eq("lead_id", leadId)
        .eq("include_in_quote", true)
        .order("date", { ascending: true, nullsFirst: false })
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });

      if (itemsError) throw itemsError;

      // Fetch products and suppliers to enrich the snapshot with images (include id for direct linking)
      const { data: products } = await supabase
        .from("products")
        .select("id, product_name, hero_image_url, secondary_image_url, image_url_3, image_url_4");

      const { data: suppliers } = await supabase
        .from("suppliers")
        .select("name, hero_image_url, website, secondary_image_url, image_url_3, image_url_4");

      // Scale items for pax before enrichment so snapshot mirrors selected option
      const scaledItemsToSnapshot = applyOverrides(
        scaleItemsForPax((itemsToSnapshot || []) as any, basePax, effectivePax)
      );

      // Fetch room rates referenced by these items for snapshot enrichment
      const roomRatesMap = await fetchRoomRatesForItems(scaledItemsToSnapshot as any);

      // Enrich items with matched product and supplier data for the snapshot using score-based matching
      const enrichedItems = scaledItemsToSnapshot.map((item: any) => {
        // Priority 1: Direct product link via product_id
        const directlyLinkedProduct = item.product_id
          ? products?.find(p => p.id === item.product_id)
          : null;
        
        if (directlyLinkedProduct) {
          const normalizedSupplierName = (item.supplier || '').trim().toLowerCase();
          const matchedSupplier = suppliers?.find((s) => 
            (s.name || '').trim().toLowerCase() === normalizedSupplierName
          );
          
          return {
            ...item,
            products: directlyLinkedProduct || null,
            suppliers: matchedSupplier || null,
            room_rate: item.room_rate_id ? roomRatesMap.get(item.room_rate_id) || null : null,
          };
        }

        // Priority 2: Fall back to score-based fuzzy matching (finds best match, not first match)
        const normalizedItemTitle = (item.title || '').trim().toLowerCase();
        const normalizedItemDesc = (item.description || '').trim().toLowerCase();

        const matchedProduct = item.item_type === 'accommodation'
          ? null
          : findBestProductMatch(
              normalizedItemTitle,
              normalizedItemDesc,
              products as ProductForMatching[] | null
            );

        // Trim supplier name for matching
        const normalizedSupplierName = (item.supplier || '').trim().toLowerCase();
        let matchedSupplier = suppliers?.find((s) => 
          (s.name || '').trim().toLowerCase() === normalizedSupplierName
        ) || null;
        
        // Fallback: match supplier by item title (accommodation items ARE suppliers)
        if (!matchedSupplier) {
          matchedSupplier = suppliers?.find((s) =>
            (s.name || '').trim().toLowerCase() === normalizedItemTitle
          ) || null;
        }

        return {
          ...item,
          products: matchedProduct || null,
          suppliers: matchedSupplier || null,
          room_rate: item.room_rate_id ? roomRatesMap.get(item.room_rate_id) || null : null,
        };
      });

      const { data: quoteNumber, error: fnError } = await supabase.rpc("generate_quote_number");
      if (fnError) throw fnError;

      const { subtotal: pricedSubtotal, transportDiscount, pctDiscountAmount, discountFixed, costBeforeMarkup: cbm, markupAmount, total } = calculatePricing(scaledItemsToSnapshot as any[]);
      const serializedFees = serializeAdditionalFees(additionalFees, cbm);

      // Snapshot the currently saved single supplement (if any) onto the quote
      let singleSupplementAmount: number | null =
        (lead as any)?.single_supplement_amount ?? null;
      if (singleSupplementAmount == null) {
        const { data: leadPricing } = await supabase
          .from("leads")
          .select("single_supplement_amount")
          .eq("id", leadId)
          .maybeSingle();
        singleSupplementAmount = (leadPricing as any)?.single_supplement_amount ?? null;
      }

      // Insert quote with itinerary snapshot
      const { data: newQuote, error: insertError } = await supabase.from("quotations").insert({
        lead_id: leadId,
        user_id: user.id,
        quote_number: quoteNumber,
        subtotal: pricedSubtotal,
        transport_discount_amount: transportDiscount,
        discount_percentage: parseFloat(values.discount_percentage || "0"),
        discount_amount: pctDiscountAmount,
        discount_fixed_amount: discountFixed,
        markup_percentage: parseFloat(values.markup_percentage),
        markup_amount: markupAmount,
        total_amount: total,
        valid_until: values.valid_until || null,
        prepared_for: values.prepared_for?.trim() || null,
        inclusions: values.inclusions || null,
        exclusions: values.exclusions || null,
        terms_and_conditions: values.terms_and_conditions || null,
        notes: values.notes || null,
        status: "draft",
        payment_status: "unpaid",
        itinerary_snapshot: withClientPrices(enrichedItems, parseFloat(values.markup_percentage) || 0),
        itinerary_layout: (lead as any)?.itinerary_layout || "day_by_day",
        itinerary_sections: (lead as any)?.itinerary_sections || [],
        show_line_items: values.show_line_items,
        cost_presentation: !values.show_line_items ? "total_only" : values.hide_markup ? "marked_up_items" : "full_breakdown",
        allow_pax_scaling: !!values.allow_pax_scaling,
        is_white_label: !!values.is_white_label,
        additional_fees: serializedFees,
        single_supplement_amount: singleSupplementAmount,
        group_leader_count: leaderCount > 0 ? leaderCount : null,

        optional_extras: serializeOptionalExtras(optionalExtras),
        hide_programme_inclusions_exclusions: !!values.hide_programme_inclusions_exclusions,
      } as any).select().single();

      if (insertError) throw insertError;

      await persistNewLibraryExtras(optionalExtras);

      // Store participant option on quotation if selected
      if (selectedOptionId) {
        await supabase
          .from("quotations")
          .update({ 
            participant_option_id: selectedOptionId,
            pax_count: selectedOptionQuantity || undefined,
          } as any)
          .eq("id", newQuote.id);
      }

      // Link included itinerary items to this quotation (matching GenerateQuoteDialog behavior)
      const itemIds = (itemsToSnapshot || []).map(item => item.id);
      if (itemIds.length > 0) {
        const { error: linkError } = await supabase
          .from("itinerary_items")
          .update({ quotation_id: newQuote.id })
          .in("id", itemIds);
        
        if (linkError) {
          console.error("Failed to link items to quotation:", linkError);
          // Don't throw - quote was created, this is supplementary
        }
      }

      const quoteId = newQuote.id;

      // Generate payment link (graceful degradation if it fails)
      try {
        const { error: paymentLinkError } = await supabase.functions.invoke(
          "generate-quote-payment-link",
          {
            body: {
              quote_id: quoteId,
              amount: total,
              quote_number: quoteNumber,
              origin_url: window.location.origin,
            },
          }
        );
        if (paymentLinkError) {
          console.error("Payment link generation failed:", paymentLinkError);
        }
      } catch (paymentError) {
        console.error("Payment link generation error:", paymentError);
      }

      await supabase
        .from("leads")
        .update({ 
          quoted_price: total,
          markup_percentage: parseFloat(values.markup_percentage),
          status: "quote_generated",
        })
        .eq("id", leadId);

      await supabase.from("activity_log").insert({
        lead_id: leadId,
        user_id: user.id,
        action: "Quote Generated",
        details: `Quote ${quoteNumber} generated with ${values.markup_percentage}% markup`,
      });

      return quoteId;
    },
    onSuccess: (quoteId) => {
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["activity-log", leadId] });
      queryClient.invalidateQueries({ queryKey: ["quotations", leadId] });
      setGeneratedQuoteId(quoteId);
      setPreviewMode(true);
      toast.success("Quote generated! Preview ready.");
    },
    onError: (error: any) => {
      toast.error(`Failed to generate quote: ${error.message}`);
    },
  });

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Quote-${generatedQuote?.quote_number || "preview"}`,
  });

  const handleShareLink = () => {
    if (generatedQuote?.share_token) {
      const link = `${PRODUCTION_DOMAIN}/quote/${generatedQuote.share_token}`;
      navigator.clipboard.writeText(link);
      toast.success("Quote link copied to clipboard!");
    }
  };

  const { transportDiscount, pctDiscountAmount, discountFixed, costBeforeMarkup, markupAmount, additionalFeesTotal, total, markupPct: effectiveMarkupPct } = calculatePricing();
  const discountPercentage = parseFloat(form.watch("discount_percentage") || "0");
  const fixedDiscountVal = parseFloat(form.watch("discount_fixed_amount") || "0");
  const transportDiscountVal = parseFloat(form.watch("transport_discount_amount") || "0");
  const currencySymbol = getCurrencySymbol(currency);

  // Block quote generation for late-stage/terminal statuses - return disabled button
  if (isBlocked) {
    const statusLabel = leadStatus!.charAt(0).toUpperCase() + leadStatus!.slice(1).replace(/_/g, ' ');
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button className="gap-2" disabled>
                <Eye className="h-4 w-4" />
                Preview & Generate Quote
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            Cannot generate quotes for leads in "{statusLabel}" status
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (isOpen) {
        // Always start fresh when opening
        setPreviewMode(false);
        setGeneratedQuoteId(null);
        setRoomOverrides({});
        setAccomPanelOpen(false);
        form.reset();
        queryClient.invalidateQueries({ queryKey: ["itinerary-items-with-images", leadId] });
      }
      if (!isOpen) {
        setPreviewMode(false);
        setGeneratedQuoteId(null);
        setRoomOverrides({});
        setAccomPanelOpen(false);
        form.reset();
      }
    }}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-quote-preview-trigger>
          <Eye className="h-4 w-4" />
          Generate Quote
        </Button>
      </DialogTrigger>
      <DialogContent className={previewMode ? "max-w-7xl max-h-[90vh]" : "max-w-2xl"}>
        <DialogHeader>
          <DialogTitle>{previewMode ? "Quote Preview" : "Generate Quote"}</DialogTitle>
        </DialogHeader>

        {!previewMode ? (
          <Form {...form}>
            <form onSubmit={form.handleSubmit((values) => generateQuoteMutation.mutate(values))} className="space-y-4">
              <HotelOptionMarginsCard
              items={applyOverrides(isScaling ? scaleItemsForPax(scalingItems as any, basePax, effectivePax) : (scalingItems as any[]))}
              currency={currency}
              priceOpts={{
                markupPct: parseFloat(form.watch("markup_percentage") || "0"),
                transportDiscount: parseFloat(form.watch("transport_discount_amount") || "0"),
                discountPct: parseFloat(form.watch("discount_percentage") || "0"),
                discountFixed: parseFloat(form.watch("discount_fixed_amount") || "0"),
                feesTotal: (c: number) => calculateAdditionalFeesTotal(additionalFees, c),
                extraCost: leaderImpact.netTotal,
              }}
            />
            <ParticipantOptionSelector
                leadId={leadId}
                selectedOptionId={selectedOptionId}
                onSelect={(id, qty, label) => {
                  setSelectedOptionId(id);
                  setSelectedOptionQuantity(qty);
                  setSelectedOptionLabel(label);
                  setRoomOverrides({});
                }}
              />

              <div className="p-4 bg-muted rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Retail Subtotal:</span>
                  <span className="font-semibold">{currencySymbol}{scaledTotal.toFixed(2)}</span>
                </div>
                {isScaling && (
                  <div className="text-xs text-muted-foreground -mt-1">
                    Scaled from {basePax} to {effectivePax} travelers
                  </div>
                )}
                {showAccomWarning && (
                  <Collapsible open={accomPanelOpen} onOpenChange={setAccomPanelOpen}>
                    <div className="flex gap-2 items-start p-2 rounded border border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200 text-xs">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span className="flex-1">Accommodation rooms aren't auto-adjusted for the new pax count.</span>
                      <CollapsibleTrigger asChild>
                        <button type="button" className="flex items-center gap-1 font-medium hover:underline">
                          {accomPanelOpen ? 'Hide' : 'Adjust rooms'}
                          <ChevronDown className={`h-3 w-3 transition-transform ${accomPanelOpen ? 'rotate-180' : ''}`} />
                        </button>
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent>
                      <AccommodationRoomOverridePanel
                        items={accommodationItems as any}
                        overrides={roomOverrides}
                        onChange={(id, rooms) => setRoomOverrides((prev) => ({ ...prev, [id]: rooms }))}
                        onReset={() => setRoomOverrides({})}
                        currencySymbol={currencySymbol}
                      />
                    </CollapsibleContent>
                  </Collapsible>
                )}
                {transportDiscountVal > 0 && (
                  <div className="flex justify-between text-sm text-destructive">
                    <span>Own Transport Discount:</span>
                    <span>-{currencySymbol}{transportDiscount.toFixed(2)}</span>
                  </div>
                )}
                {discountPercentage > 0 && (
                  <div className="flex justify-between text-sm text-destructive">
                    <span>Trade Discount ({discountPercentage}%):</span>
                    <span>-{currencySymbol}{pctDiscountAmount.toFixed(2)}</span>
                  </div>
                )}
                {fixedDiscountVal > 0 && (
                  <div className="flex justify-between text-sm text-destructive">
                    <span>Trade Discount (Fixed):</span>
                    <span>-{currencySymbol}{discountFixed.toFixed(2)}</span>
                  </div>
                )}
                {(transportDiscountVal > 0 || discountPercentage > 0 || fixedDiscountVal > 0) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Net Programme Cost:</span>
                    <span className="font-semibold">{currencySymbol}{costBeforeMarkup.toFixed(2)}</span>
                  </div>
                )}
                {additionalFees.filter(f => f.percentage > 0).map((fee, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{fee.label} ({fee.percentage}%):</span>
                    <span className="font-semibold">{currencySymbol}{(costBeforeMarkup * (fee.percentage / 100)).toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {usesItemMarkups
                      ? `Markup (blended ${effectiveMarkupPct.toFixed(1)}%):`
                      : `Markup (${form.watch("markup_percentage")}%):`}
                  </span>
                  <span className="font-semibold text-primary">{currencySymbol}{markupAmount.toFixed(2)}</span>
                </div>

                <div className="flex justify-between pt-2 border-t">
                  <span className="font-bold">Total Investment:</span>
                  <span className="font-bold text-lg">{currencySymbol}{total.toFixed(2)}</span>
                </div>
              </div>

              <FormField
                control={form.control}
                name="transport_discount_amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Own Transport Discount ({currency})</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" min="0" placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="discount_percentage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trade Discount (%)</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.00001" min="0" max="100" placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="discount_fixed_amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trade Discount - Fixed ({currency})</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" min="0" placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <AdditionalFeesSection
                fees={additionalFees}
                onChange={setAdditionalFees}
                costBeforeMarkup={costBeforeMarkup}
                currencySymbol={currencySymbol}
              />

              <OptionalExtrasSection
                extras={optionalExtras}
                onChange={setOptionalExtras}
                currencySymbol={currencySymbol}
              />

              <FormField
                control={form.control}
                name="markup_percentage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Markup Percentage (%)</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.00001" min="0" max="100" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="valid_until"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <DatePickerField
                      label="Valid Until"
                      value={field.value ? new Date(field.value) : undefined}
                      onChange={(date) => field.onChange(date ? format(date, "yyyy-MM-dd") : "")}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="prepared_for"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prepared For (optional)</FormLabel>
                    <FormControl>
                      <Input placeholder={lead?.client_name || "Client name"} {...field} />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      Leave blank to use the lead client name{lead?.client_name ? ` (${lead.client_name})` : ""}.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
              <FormField
                control={form.control}
                name="hide_programme_inclusions_exclusions"
                render={({ field }) => (
                  <FormItem className="flex items-start justify-between gap-3 space-y-0">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Programme Inclusions & Exclusions</FormLabel>
                      <p className="text-sm text-muted-foreground">
                        {field.value
                          ? "Hidden on the quote. Each activity's own inclusions and exclusions still appear under it."
                          : "Shown at the end of the quote. Activity-level details still appear under each activity."}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm text-muted-foreground">Hide on quote</span>
                      <FormControl>
                        <Switch
                          checked={!!field.value}
                          onCheckedChange={(v) => {
                            hideToggledRef.current = true;
                            field.onChange(v);
                          }}
                        />
                      </FormControl>
                    </div>
                  </FormItem>
                )}
              />

              {!form.watch("hide_programme_inclusions_exclusions") && (
              <div className="space-y-4 border-t pt-4">
                {prefilledFromPrevious && (
                  <p className="text-xs text-muted-foreground italic">
                    Loaded from your previous quote for this lead — edit as needed.
                  </p>
                )}
                {prefillSettled ? (
                <InclusionsExclusionsSelector
                  itineraryItems={scalingItems as ItineraryItemForExtraction[]}
                  inclusionsValue={form.watch("inclusions") || ""}
                  exclusionsValue={form.watch("exclusions") || ""}
                  onInclusionsChange={(val) => form.setValue("inclusions", val, { shouldDirty: true, shouldTouch: true })}
                  onExclusionsChange={(val) => form.setValue("exclusions", val, { shouldDirty: true, shouldTouch: true })}
                  isLoadingItems={isLoadingScalingItems}
                  onRefreshItems={async () => {
                    const res = await refetchScalingItems();
                    return (res.data || []) as ItineraryItemForExtraction[];
                  }}
                />
                ) : (
                  <p className="text-xs text-muted-foreground">Loading saved selections…</p>
                )}
              </div>
              )}
              </div>

              <FormField
                control={form.control}
                name="terms_and_conditions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Payment Terms</FormLabel>
                    <FormControl>
                      {prefillSettled ? (
                        <TermsAndConditionsSelector
                          value={field.value || ""}
                          onChange={field.onChange}
                        />
                      ) : (
                        <p className="text-xs text-muted-foreground">Loading saved selections…</p>
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Internal Notes</FormLabel>
                    <FormControl>
                      <Textarea rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="is_white_label"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">White Label</FormLabel>
                      <p className="text-sm text-muted-foreground">
                        Hides our logo, company name and contact details. For a client logo, use "Branded Quote" on the quote card.
                      </p>
                    </div>
                    <FormControl>
                      <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="allow_pax_scaling"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Let client change number of travellers</FormLabel>
                      <p className="text-sm text-muted-foreground">
                        Client can increase the group size; the total scales at the same per-person rate. Leave off when rates drop for bigger groups.
                      </p>
                    </div>
                    <FormControl>
                      <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="show_line_items"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Show Line Item Breakdown</FormLabel>
                      <p className="text-sm text-muted-foreground">
                        When disabled, clients see only the total investment
                      </p>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              {form.watch("show_line_items") && (
                <FormField
                  control={form.control}
                  name="hide_markup"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Show items at marked-up rates</FormLabel>
                        <p className="text-sm text-muted-foreground">
                          Each item and section shows its client price; the service fee and markup are not shown
                        </p>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={generateQuoteMutation.isPending}>
                  {generateQuoteMutation.isPending ? "Generating..." : "Generate & Preview"}
                </Button>
              </div>
            </form>
          </Form>
        ) : (
          <>
            <div className="flex gap-2 mb-4 no-print">
              <Button onClick={handlePrint} className="gap-2">
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
              <Button onClick={handleShareLink} variant="outline" className="gap-2">
                <Share2 className="h-4 w-4" />
                Copy Link
              </Button>
              <Button onClick={() => setOpen(false)} variant="outline">
                Close
              </Button>
            </div>

            <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
              {generatedQuote && (
                <QuoteDocument
                  ref={printRef}
                  quote={generatedQuote}
                  itineraryItems={itineraryItems}
                />
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
